import { Message } from '@wegic/ui';
import '@wegic/ui/dist/components/Message/index.css';
import { useEffect } from 'react';
import { reloadSandboxPreview, setTargetLayer } from '@/helpers/preview/helper';
import { syncVisitorCreditsFromDetail } from '@/helpers/visitorCredits';
import { navigate } from '@/modules/CustomRouter/handler';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';
import { getProjectDetail, getProjectSettings } from '@/services/project';
import { usePreviewStore } from '@/stores/preview';
import { ProjectStatus, Stage, useProjectStore } from '@/stores/project';
import { useRecentProjectStore } from '@/stores/recentProject';
import { useUserStore } from '@/stores/user';
import { applyProjectPreviewAvailability, beginProjectPreviewRefresh, getProjectPreviewAvailability } from './helpers/projectPreview/helper';
import { ensureSandbox, resetSandboxEnsure } from './helpers/sandbox';
let projectSessionGeneration = 0;
function isCurrentProjectSession(projectId, sessionGeneration) {
    return useProjectStore.getState().projectId === projectId && projectSessionGeneration === sessionGeneration;
}
export function applyProjectDetail(detail, isLogin) {
    useProjectStore.getState().setCurrentProjectDetail(detail);
    usePreviewStore.getState().setAvailableRoutes(detail.routes);
    useRecentProjectStore.getState().upsertRecentProject(detail);
    if (!isLogin) {
        syncVisitorCreditsFromDetail(detail);
    }
}
/**
 * 静默同步当前项目快照，用于页面恢复可见等非轮询恢复场景。
 */ export async function syncLatestProjectDetail(projectId) {
    try {
        const { data } = await getProjectDetail(projectId);
        if (useProjectStore.getState().projectId !== projectId) {
            return false;
        }
        applyProjectDetail(data, useUserStore.getState().isLogin);
        return true;
    } catch  {
        return false;
    }
}
/**
 * 初始化项目会话。已生成项目复用 Preview 可用性决策，未生成项目直接准备 Sandbox。
 */ export async function startProjectSession(projectId) {
    const sessionGeneration = ++projectSessionGeneration;
    const { isNewlyCreated, setStage, setSandboxStatus, setUserId, setSessionId, setErrorMessage, setIsNewlyCreated } = useProjectStore.getState();
    try {
        var _useUserStore_getState_userInfo;
        setStage(Stage.Created);
        setSandboxStatus('unknown');
        const userId = ((_useUserStore_getState_userInfo = useUserStore.getState().userInfo) === null || _useUserStore_getState_userInfo === void 0 ? void 0 : _useUserStore_getState_userInfo.userId) || 'system';
        setUserId(userId);
        const generatedSessionId = `chat-${projectId}-${userId}`;
        setSessionId(generatedSessionId);
        const [{ data: projectDetail }, previewAvailability] = await Promise.all([
            getProjectDetail(projectId),
            getProjectPreviewAvailability(projectId)
        ]);
        if (!isCurrentProjectSession(projectId, sessionGeneration)) {
            return;
        }
        applyProjectDetail(projectDetail, useUserStore.getState().isLogin);
        if (projectDetail.status === ProjectStatus.GENERATED) {
            beginProjectPreviewRefresh();
            applyProjectPreviewAvailability(projectId, previewAvailability);
            setStage(Stage.SandboxInfoGot);
            setStage(Stage.SessionStarted);
            setStage(Stage.SandboxReady);
            setStage(Stage.Ready);
            setIsNewlyCreated(false);
            return;
        }
        await ensureSandbox(projectId, generatedSessionId, userId);
        if (!isCurrentProjectSession(projectId, sessionGeneration)) {
            return;
        }
        setStage(Stage.SandboxInfoGot);
        setStage(Stage.SessionStarted);
        setTargetLayer('live');
        setStage(Stage.SandboxReady);
        setStage(Stage.Ready);
        setIsNewlyCreated(false);
    } catch (error) {
        if (!isCurrentProjectSession(projectId, sessionGeneration)) {
            return;
        }
        console.error('启动项目失败:', error);
        const errorMsg = error instanceof Error ? error.message : String(error);
        setErrorMessage(errorMsg);
        setStage(Stage.Error);
        // 出错时也清除标识，确保重试时能正常启动
        if (isNewlyCreated) {
            setIsNewlyCreated(false);
        }
        const { isLogin } = useUserStore.getState();
        isLogin && Message.error(intl.get('project.error.startupFailed', {
            error: errorMsg
        }));
    }
}
/**
 * 重置项目会话状态。
 */ export function resetProjectSession() {
    projectSessionGeneration += 1;
    const { setErrorMessage, setStage } = useProjectStore.getState();
    setErrorMessage('');
    setStage(Stage.Input);
    resetSandboxEnsure();
}
/**
 * 重启当前项目会话。
 */ export function restartProjectSession() {
    const { projectId: currentProjectId } = useProjectStore.getState();
    if (!currentProjectId) {
        return;
    }
    resetProjectSession();
    startProjectSession(currentProjectId);
}
/**
 * 根据项目id，进行对应网站设置的加载
 * 当前网站设置需要涉及两个后端接口才能获取到完整信息，一个是项目详情，一个是项目设置
 * 目前使用的场景：SettingsModal 组件里面的 Site Setting 内容预加载
 * 项目详情对匿名用户也开放，仅 settings 限制登录态
 * @param projectId 项目id
 * @param isLogin 是否已登录
 */ export function useLoadSiteSettings(projectId) {
    let isLogin = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
    const currentProjectDetail = useProjectStore((state)=>state.currentProjectDetail);
    useEffect(()=>{
        if (!currentProjectDetail && projectId) {
            getProjectDetail(projectId).then((res)=>{
                applyProjectDetail(res.data, isLogin);
            }).catch(()=>{
                const { isLogin } = useUserStore.getState();
                if (isLogin) {
                    Message.error(intl.get('project.error.notFound'));
                    navigate('/workspace');
                }
            });
        }
    }, [
        projectId,
        currentProjectDetail,
        isLogin
    ]);
    const currentProjectSettings = useProjectStore((state)=>state.currentProjectSettings);
    const setCurrentProjectSettings = useProjectStore((state)=>state.setCurrentProjectSettings);
    useEffect(()=>{
        if (!currentProjectSettings && projectId && isLogin) {
            getProjectSettings(projectId).then((res)=>{
                setCurrentProjectSettings(res.data);
            }).catch(()=>{
                const { isLogin } = useUserStore.getState();
                isLogin && Message.error(intl.get('project.error.fetchSettingsFailed'));
            });
        }
    }, [
        projectId,
        currentProjectSettings,
        isLogin
    ]);
    useEffect(()=>{
        return ()=>{
            useProjectStore.getState().setCurrentProjectDetail(null);
            setCurrentProjectSettings(null);
        };
    }, []);
}
/**
 * 监听网站生成完成事件,并更新最新的项目信息
 * 当网站生成完成时,重新获取项目详情以确保项目名称等信息是最新的
 */ export function useUpdateProjectOnGeneration() {
    useEffect(()=>{
        const handledGenerationEvents = new Set();
        const callback = (data)=>{
            const eventProjectId = data.projectId;
            if (!eventProjectId) {
                return;
            }
            const { projectId } = useProjectStore.getState();
            if (projectId && projectId !== eventProjectId) {
                return;
            }
            const eventKey = `${eventProjectId}:${data.messageId}`;
            if (handledGenerationEvents.has(eventKey)) {
                return;
            }
            handledGenerationEvents.add(eventKey);
            const projectStore = useProjectStore.getState();
            const isInitialGeneration = projectStore.status === ProjectStatus.GENERATING;
            const previewStore = usePreviewStore.getState();
            previewStore.setPendingGenerationReveal(true);
            if (isInitialGeneration) {
                previewStore.setInitialGenerationRevealPending(true);
                previewStore.setLivePreviewProblem(undefined);
                previewStore.setIsPostGenRefreshLoading(false);
            }
            if (projectStore.currentProjectDetail) {
                projectStore.setCurrentProjectDetail({
                    ...projectStore.currentProjectDetail,
                    status: ProjectStatus.GENERATED
                });
            }
            projectStore.setStatus(ProjectStatus.GENERATED);
            if (isInitialGeneration) {
                projectStore.setErrorMessage('');
                reloadSandboxPreview();
            }
            getProjectDetail(eventProjectId).then((res)=>{
                if (useProjectStore.getState().projectId !== eventProjectId) {
                    return;
                }
                const previewStore = usePreviewStore.getState();
                const projectDetail = {
                    ...res.data,
                    status: ProjectStatus.GENERATED
                };
                applyProjectDetail(projectDetail, useUserStore.getState().isLogin);
                useProjectStore.getState().setStatus(projectDetail.status);
                useProjectStore.getState().setProjectPanelMode('preview');
                if (!isInitialGeneration) {
                    previewStore.setIsPostGenRefreshLoading(false);
                    reloadSandboxPreview();
                }
            }).catch((error)=>{
                console.error('获取项目详情失败:', error);
            });
        };
        globalEventEmitter.on(EVENTS.WEBSITE_GENERATION_COMPLETED, callback);
        return ()=>{
            globalEventEmitter.off(EVENTS.WEBSITE_GENERATION_COMPLETED, callback);
        };
    }, []);
}
